import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import css from './ViewModeToggle.module.scss';

/**
 * Canonical view-mode type, shared by the desktop toggle and the mobile
 * {@link MobileViewModeButton}. Both the Library page (media) and the Libraries
 * page (libraries) use these two modes, but they persist to *different*
 * preference keys — hence the controlled `value`/`onChange` below rather than
 * either component reaching into a store itself.
 */
export type ViewMode = 'list' | 'grid';

export const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  list: 'List view',
  grid: 'Gallery view',
};

interface ViewModeToggleProps {
  value: ViewMode;
  /** Fired only on an actual change — never with the already-active mode. */
  onChange: (mode: ViewMode) => void;
}

/**
 * Both modes side by side, the current one lit: a flat two-button switch.
 *
 * It replaced a single bevelled circle that showed the current mode and flipped on click. Saving
 * 34px of header was why, and the cost was that a lone glyph read as either "you are in gallery
 * view" or "press for gallery view", so it needed its tooltip to make sense. Autoplay, Filters and
 * Sort have since left the header for the search line, so the room is there for both glyphs.
 *
 * `MobileViewModeButton` still shows only the current mode, and opens a sheet that names both.
 */
export const ViewModeToggle = ({ value, onChange }: ViewModeToggleProps) => (
  <div className={css.toggle} role="group" aria-label="View">
    {(['grid', 'list'] as const).map((mode) => (
      <Tooltip key={mode} title={VIEW_MODE_LABELS[mode]} arrow placement="top">
        <button
          type="button"
          className={classNames(css.option, value === mode && css.active)}
          aria-label={VIEW_MODE_LABELS[mode]}
          aria-pressed={value === mode}
          onClick={() => value !== mode && onChange(mode)}
        >
          {mode === 'grid' ? <ViewModuleIcon /> : <ViewListIcon />}
        </button>
      </Tooltip>
    ))}
  </div>
);
